import { useCallback, useEffect, useRef, useState } from "react";
import {
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { useSelector } from "react-redux";
import SearchInput from "../../components/Forms/SearchInput";
import { items, orderStatus } from "../../constant";
import { useApiCall } from "../../services/api";
import { formatDate, handleRouteNav, roundDecimal } from "../../utils";
import Loader from "../../common/Loader";
import Filters from "../../components/Filters";
import { Card, Pagination } from "antd";
import Paragraph from "antd/es/typography/Paragraph";
import Title from "antd/es/typography/Title";
import { PiRowsPlusTop, PiRowsPlusTopFill } from "react-icons/pi";
import { UpOutlined } from "@ant-design/icons";
import PrimaryButton from "../../components/Forms/PrimaryButton";
import { useGlobalModal } from "../../context/SuccessModalProvider";

const CustomerOrders = () => {
  const { postApi } = useApiCall();
  const { appliedFilters, DrawerUI, FilterButton, resetFilters, hasFilters } =
    Filters({
      filters: [
        {
          type: "select",
          key: "status",
          label: "Order Status",
          options: Object.entries(orderStatus).map(([key, value]) => ({
            label: value,
            value: value,
          })),
        },
        {
          type: "daterange",
          key: "orderPlaced",
          label: "Order Placed",
        },
        {
          type: "daterange",
          key: "dispatchDate",
          label: "Dispatch Date",
        },
        {
          type: "select",
          key: "quantity",
          label: "Total Quantity",
          options: [
            { label: "< 1000m", value: "-1000" },
            { label: "1000 - 5000m", value: "1000-5000" },
            { label: "5000 - 10000m", value: "5000-10000" },
            { label: "> 10000m", value: "10000-" },
          ],
        },
      ],
      onChange: () => setPagination((prev) => ({ ...prev, current: 1 })),
    });

  const {
    is_sales_person,
    is_sales_manager,
    user_id,
    is_customer,
    role_id,
    customer_id,
  } = useSelector((state) => state.auth);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [orderData, setOrderData] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
  });

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [page, setPage] = useState(1);
  const perPage = 12;

  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const loaderRef = useRef(null);
  const fetchIdRef = useRef(0);
  const { showPopup } = useGlobalModal();

  const fetchOrders = useCallback(async () => {
    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;
    try {
      setIsFetchingMore(true);
      if (page === 1) setLoading(true);
      const { status, quantity, orderPlaced, dispatchDate } = appliedFilters;
      const [minTotalQty, maxTotalQty] = quantity
        ? quantity.split("-").map(Number)
        : [];

      const filters = {
        status,
        minTotalQty: minTotalQty || undefined,
        maxTotalQty: maxTotalQty || undefined,
        start_date: orderPlaced?.[0]
          ? formatDate(new Date(orderPlaced[0]), { format: "YYYY-MM-DD" })
          : undefined,
        end_date: orderPlaced?.[1]
          ? formatDate(new Date(orderPlaced[1]), { format: "YYYY-MM-DD" })
          : undefined,
        start_dispatch_date: dispatchDate?.[0]
          ? formatDate(new Date(dispatchDate[0]), { format: "YYYY-MM-DD" })
          : undefined,
        end_dispatch_date: dispatchDate?.[1]
          ? formatDate(new Date(dispatchDate[1]), { format: "YYYY-MM-DD" })
          : undefined,
      };

      const response = await postApi(
        "order/get-order-with-customer-id",
        {
          customer_id,
          search: query,
          page,
          per_page: perPage,
          ...(Object.keys(appliedFilters).length ? filters : {}),
          ...(is_sales_person ? { user_id } : {}),
        },
        null,
      );

      if (currentFetchId !== fetchIdRef.current) {
        return;
      }

      if (!response.status && (response.is_blocked || response.is_deleted)) {
        return showPopup({
          title: null,
          content: (
            <span className="custom-modal-title">{response.message}</span>
          ),
          onOk: () => console.log("Confirmed!"),
          className: "custom-success-modal",
        });
      }

      const formattedRows = (response?.data || []).map((order) => {
        const uniquePids = new Set();
        order.items?.forEach((item) => uniquePids.add(item.sort_no));

        let first_item_name = order.items[0]?.short_name;
        const displayProduct =
          uniquePids.size > 1
            ? `${first_item_name} + ${uniquePids.size - 1} more`
            : first_item_name || "-";

        const total_qty = order.items.reduce(
          (t, i) => t + (+i.update_total_meter || +i.quantity_meters),
          0,
        );

        return {
          ...order,
          clientName: order.customer?.company_name || "-",
          products: displayProduct,
          totalCases: order.items.reduce(
            (t, i) => t + (+i.update_total_cases || +i.cases),
            0,
          ),
          totalQuantityMeters: roundDecimal(total_qty),
        };
      });

      setOrderData((prev) =>
        page === 1 ? formattedRows : [...prev, ...formattedRows],
      );
      setTotalOrders(response?.total || 0);
      setHasMore(formattedRows.length === perPage);
    } catch (error) {
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
    } finally {
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      setLoading(false);
      setIsFetchingMore(false);
    }
  }, [
    appliedFilters,
    customer_id,
    is_sales_person,
    page,
    perPage,
    query,
    user_id,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchOrders();
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [fetchOrders]);

  const handleChange = (paginationInfo, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    });
  };

  const onSearch = (value, _e, info) => console.log(info?.source, value);

  const handleOnSearchChange = (e) => {
    const value = e.target.value;
    setSearchParams(
      (prev) => {
        if (value) prev.set("q", value);
        else prev.delete("q");
        return prev;
      },
      { replace: true },
    );
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleMenuClick = (e) => {
    console.log("click", e);
  };

  const handleRow = (record) => {
    navigate(handleRouteNav(`/orders/${record.order_id}`, role_id));
  };

  const { setTitle, setDescription, setBackButton } = useOutletContext();

  useEffect(() => {
    if (!is_customer) {
      setTitle("Orders");
      setDescription(`${totalOrders} total orders`);
      setBackButton(false);
    }
  }, [setTitle, setDescription, setBackButton, totalOrders]);

  useEffect(() => {
    setPage(1);
  }, [query, appliedFilters]);

  useEffect(() => {
    if (!loaderRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isFetchingMore &&
          orderData.length > 0
        ) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 1 },
    );

    observer.observe(loaderRef.current);

    return () => observer.disconnect();
  }, [hasMore, isFetchingMore]);

  return (
    <>
      <DrawerUI />
      <div
        className="bg-[#FFFFFF] mt-3"
        style={{ borderRadius: "32px", overflow: "hidden" }}
      >
        <div className="flex justify-between gap-7 items-center px-8 py-6">
          <SearchInput
            onSearch={onSearch}
            value={query}
            onChange={handleOnSearchChange}
            placeholder="Search for orders"
          />
          <div className="flex gap-3 items-center">
            <FilterButton />
            {hasFilters && (
              <PrimaryButton
                btnType="secondary"
                text="Remove"
                onClick={() => resetFilters()}
                textClassName=""
                className="!w-[130px]"
              />
            )}
          </div>
        </div>
        <>
          {orderData?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-4 pt-0 px-6 pb-6">
              {orderData.map((item, i) => (
                <div
                  className="bg-white rounded-xl flex flex-row border hover:shadow-lg duration-300 cursor-pointer"
                  key={i}
                  onClick={() => handleRow(item)}
                >
                  <div className="w-[150px] h-auto overflow-hidden rounded-l-xl flex items-center p-2">
                    <img
                      src={item?.items?.[0]?.product_picture_url}
                      alt="card-logo"
                      className="w-full h-full rounded-xl flex-1 object-cover"
                    />
                  </div>
                  <div
                    className="p-4 flex flex-col justify-between gap-5 flex-1 cursor-pointer text-start"
                    key={i}
                  >
                    <div>
                      <Paragraph className="!mb-0">
                        Order ID: #{item.order_id}
                      </Paragraph>
                      <Title level={5} className="!m-0">
                        {item.products}
                      </Title>
                    </div>

                    <div className="flex flex-col gap-3">
                      {item.status === orderStatus.production && (
                        <div className="flex gap-3 items-center">
                          <div className="w-[50px] h-[50px] bg-[#A334341A] rounded-full flex items-center justify-center">
                            <img
                              src="/assets/icons/GearFine.png"
                              alt="Cube"
                              className="w-auto h-[30px] object-contain"
                            />
                          </div>

                          <div className="flex flex-col items-center">
                            <span className="text-gray-500">In Production</span>
                          </div>
                        </div>
                      )}

                      {item.status === orderStatus.dispatched && (
                        <div className="flex gap-3 items-center">
                          <div className="w-[50px] h-[50px] bg-[#A334341A] rounded-full flex items-center justify-center">
                            <img
                              src="/assets/icons/TruckTrailer.png"
                              alt="Cube"
                              className="w-auto h-[30px] object-contain"
                            />
                          </div>

                          <div className="flex flex-col text-start">
                            <span className="text-gray-500">Dispatched:</span>
                            <span className="text-start text-black">
                              {formatDate(item?.dispatch_date, {
                                format: "D MMM YYYY",
                              })}
                            </span>
                          </div>
                        </div>
                      )}

                      {item.status === orderStatus.parts_dispatched && (
                        <div className="flex gap-3 items-center">
                          <div className="w-[50px] h-[50px] bg-[#A334341A] rounded-full flex items-center justify-center">
                            <img
                              src="/assets/icons/TruckTrailer.png"
                              alt="Cube"
                              className="w-auto h-[30px] object-contain"
                            />
                          </div>

                          <div className="flex flex-col text-start">
                            <span className="text-gray-500">
                              Part Dispatched:
                            </span>
                            <span className="text-start text-black">
                              {formatDate(item?.dispatch_date, {
                                format: "D MMM YYYY",
                              })}
                            </span>
                          </div>
                        </div>
                      )}

                      {item.status === orderStatus.cancelled && (
                        <div className="flex gap-3 items-center">
                          <div className="w-[50px] h-[50px] bg-[#A334341A] rounded-full flex items-center justify-center">
                            <img
                              src="/assets/icons/Prohibit.png"
                              alt="Cube"
                              className="w-auto h-[30px] object-contain"
                            />
                          </div>

                          <div className="flex flex-col text-start">
                            <span className="text-gray-500">Cancelled:</span>
                            <span className="text-start text-black">
                              {formatDate(item?.cancel_date, {
                                format: "D MMM YYYY",
                              })}
                            </span>
                          </div>
                        </div>
                      )}

                      {item.status === orderStatus.parts_cancelled && (
                        <div className="flex gap-3 items-center">
                          <div className="w-[50px] h-[50px] bg-[#A334341A] rounded-full flex items-center justify-center">
                            <img
                              src="/assets/icons/Prohibit.png"
                              alt="Cube"
                              className="w-auto h-[30px] object-contain"
                            />
                          </div>

                          <div className="flex flex-col text-start">
                            <span className="text-gray-500">
                              Part Cancelled:
                            </span>
                            <span className="text-start text-black">
                              {formatDate(item?.cancel_date, {
                                format: "D MMM YYYY",
                              })}
                            </span>
                          </div>
                        </div>
                      )}

                      {item.status === orderStatus.placed && (
                        <div className="flex gap-3 items-center">
                          <div className="w-[50px] h-[50px] bg-[#A334341A] rounded-full flex items-center justify-center">
                            <img
                              src="/assets/icons/cube.png"
                              alt="Cube"
                              className="w-auto h-[30px] object-contain"
                            />
                          </div>

                          <div className="flex flex-col text-start">
                            <span className="text-gray-500">Order Placed:</span>
                            <span className="text-start text-black">
                              {formatDate(item?.created_at, {
                                format: "D MMM YYYY",
                              })}
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-3 items-center">
                        <div className="w-[50px] h-[50px] bg-[#A334341A] rounded-full flex items-center justify-center">
                          <PiRowsPlusTopFill className="w-auto h-[30px] object-contain text-[#A33434]" />
                        </div>

                        <div className="flex flex-col text-start">
                          <span className="text-gray-500">Total Quantity:</span>
                          <span className="text-start text-black">{`${item.totalCases} cases (${item.totalQuantityMeters}m)`}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {orderData?.length == 0 && !loading && (
            <>
              <div className="flex justify-center">
                <div>
                  <img src="/assets/icons/EmptySearch.svg"></img>
                  <p className="text-[#000] mb-8">
                    Sorry! We couldn't find any matches.
                  </p>
                </div>
              </div>
            </>
          )}
        </>

        {/* <div className="my-4 flex w-full justify-end">
          <Pagination
            total={totalOrders}
            limit={pagination.pageSize}
            page={pagination.current}
            onChange={(page) => {
              setPagination((prev) => ({ ...prev, current: page }));
            }}
          />
        </div> */}
        <div ref={loaderRef} className="flex justify-center py-6">
          {(loading || isFetchingMore) && <Loader size={40} />}
        </div>
      </div>
    </>
  );
};

export default CustomerOrders;
